import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, MapPin, CheckCircle2, FileText, Users, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "@/context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "/images/ym6gev0b_AE8ECFCA-9BBA-44D9-A534-DED3210EC5A4.png";

export default function CampRegistration() {
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const preselectedCampId = searchParams.get("camp");
  
  const [camps, setCamps] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showWaiver, setShowWaiver] = useState(false);
  
  const [formData, setFormData] = useState({
    camp_id: preselectedCampId || "",
    camp_name: "",
    participant_name: "",
    gender: "",
    age: "",
    parent_name: "",
    parent_email: "",
    parent_phone: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    medical_info: "",
    skill_level: "",
    waiver_signed: false,
    waiver_signer_name: "",
    waiver_signed_date: "",
    photo_consent: false,
  });

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchCamps();
  }, []);

  useEffect(() => {
    if (preselectedCampId && camps.length > 0) {
      const camp = camps.find(c => c.id === preselectedCampId);
      if (camp) {
        setFormData(prev => ({ ...prev, camp_id: camp.id, camp_name: camp.name }));
      }
    }
  }, [preselectedCampId, camps]);

  const fetchCamps = async () => {
    try {
      const response = await axios.get(`${API}/camps`);
      // Hide camps that have already ended — they're not registerable.
      const today = new Date().toISOString().slice(0, 10);
      const upcoming = (response.data || []).filter(
        (c) => !c.end_date || c.end_date >= today
      );
      setCamps(upcoming);
    } catch (error) {
      console.error("Error fetching camps:", error);
      toast.error("Failed to load camps");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCampChange = (campId) => {
    const camp = camps.find(c => c.id === campId);
    if (camp) {
      setFormData(prev => ({ ...prev, camp_id: campId, camp_name: camp.name }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.camp_id) {
      toast.error("Please select a camp");
      return;
    }
    
    if (!formData.waiver_signed || !formData.waiver_signer_name) {
      toast.error("Please read and sign the waiver to complete registration");
      return;
    }

    setIsSubmitting(true);
    try {
      // Get current language from localStorage
      const currentLanguage = localStorage.getItem('burns-elite-language') || 'en';
      
      await axios.post(`${API}/registrations`, {
        ...formData,
        age: parseInt(formData.age),
        waiver_signed_date: new Date().toISOString().split('T')[0],
        language: currentLanguage
      });
      setIsSuccess(true);
      toast.success("Registration successful!");
    } catch (error) {
      console.error("Error submitting registration:", error);
      toast.error("Failed to submit registration. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-burns-black flex items-center justify-center p-4" data-testid="registration-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <CheckCircle2 className="w-20 h-20 text-burns-gold mx-auto mb-6" />
          <h1 className="font-heading text-4xl font-bold text-white mb-4">{t('registration.successTitle')}</h1>
          <p className="text-zinc-400 mb-8">
            {t('registration.successMessage')}
          </p>
          <Link to="/">
            <Button className="btn-primary px-8 py-4 rounded-sm">
              {t('registration.backToHome')}
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-burns-black" data-testid="camp-registration-page">
      {/* Header */}
      <header className="nav-glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="back-link">
            <ArrowLeft className="w-5 h-5 text-zinc-400" />
            <img src={LOGO_URL} alt="Burns Elite Baseball" className="h-12 w-auto" />
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-4 text-center" data-testid="registration-title">
            {t('registration.title')} <span className="text-burns-red">{t('registration.titleAccent')}</span>
          </h1>
          <p className="text-zinc-400 text-center mb-12">
            {t('registration.subtitle')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-8" data-testid="registration-form">
            {/* Camp Selection */}
            <div className="bg-zinc-950 border border-white/10 rounded-sm p-6">
              <h2 className="font-heading text-xl font-bold text-burns-gold mb-4">{t('registration.selectCamp')}</h2>
              
              {isLoading ? (
                <p className="text-zinc-400">{t('common.loading')}</p>
              ) : camps.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-burns-gold mx-auto mb-4" />
                  <p className="text-zinc-400">No camps currently available. Check back soon!</p>
                </div>
              ) : (
                <Select value={formData.camp_id} onValueChange={handleCampChange} data-testid="camp-select">
                  <SelectTrigger className="bg-transparent border-white/20 text-white h-12">
                    <SelectValue placeholder={t('registration.selectCampPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/20">
                    {camps.map(camp => {
                      const spotsLeft = (camp.max_participants || 30) - (camp.current_participants || 0);
                      const isAlmostFull = spotsLeft <= 5;
                      return (
                        <SelectItem
                          key={camp.id}
                          value={camp.id}
                          className="text-white focus:bg-burns-red focus:text-white data-[state=checked]:text-white"
                        >
                          <div className="flex flex-col items-start">
                            <span className="font-bold text-white">{camp.name}</span>
                            <span className="text-xs text-zinc-200">
                              {camp.start_date} - {camp.end_date} | {camp.age_group}
                              {isAlmostFull && <span className="text-red-300 ml-2">• Only {spotsLeft} left!</span>}
                            </span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              )}

              {formData.camp_id && camps.find(c => c.id === formData.camp_id) && (
                <div className="mt-4 p-4 bg-burns-dark rounded-sm border border-burns-gold/30">
                  {(() => {
                    const selectedCamp = camps.find(c => c.id === formData.camp_id);
                    const spotsLeft = (selectedCamp.max_participants || 30) - (selectedCamp.current_participants || 0);
                    const isFilling = spotsLeft <= 10;
                    const isAlmostFull = spotsLeft <= 5;
                    
                    return (
                      <>
                        <h3 className="font-heading text-lg text-white mb-2">{selectedCamp.name}</h3>
                        <p className="text-zinc-400 text-sm mb-2">{selectedCamp.description}</p>
                        <div className="flex flex-wrap gap-4 text-sm text-zinc-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {selectedCamp.start_date} - {selectedCamp.end_date}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {selectedCamp.location}
                          </span>
                        </div>
                        
                        {/* Capacity Counter */}
                        <div className={`mt-4 p-3 rounded-sm flex items-center gap-2 ${
                          isAlmostFull 
                            ? 'bg-red-500/20 border border-red-500/30' 
                            : isFilling 
                              ? 'bg-yellow-500/20 border border-yellow-500/30'
                              : 'bg-burns-gold/10 border border-burns-gold/30'
                        }`}>
                          {isAlmostFull ? (
                            <AlertTriangle className="w-5 h-5 text-red-400" />
                          ) : (
                            <Users className="w-5 h-5 text-burns-gold" />
                          )}
                          <span className={`font-medium ${isAlmostFull ? 'text-red-400' : isFilling ? 'text-yellow-400' : 'text-burns-gold'}`}>
                            {spotsLeft <= 0 
                              ? "Camp is FULL! Join the waitlist instead."
                              : isAlmostFull 
                                ? `Only ${spotsLeft} spot${spotsLeft !== 1 ? 's' : ''} left! Register now!`
                                : isFilling
                                  ? `Filling up fast! ${spotsLeft} spots remaining.`
                                  : `${spotsLeft} spots available`
                            }
                          </span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Participant Information */}
            <div className="bg-zinc-950 border border-white/10 rounded-sm p-6">
              <h2 className="font-heading text-xl font-bold text-burns-gold mb-4">{t('registration.participantInfo')}</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="participant_name" className="text-zinc-400 mb-2 block">{t('registration.participantName')} *</Label>
                  <Input
                    id="participant_name"
                    type="text"
                    value={formData.participant_name}
                    onChange={(e) => handleChange("participant_name", e.target.value)}
                    className="form-input bg-transparent border-b border-white/20 rounded-none focus:border-burns-red"
                    required
                    data-testid="participant-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="gender" className="text-zinc-400 mb-2 block">{t('registration.gender')} *</Label>
                  <Select value={formData.gender} onValueChange={(v) => handleChange("gender", v)} data-testid="gender-select">
                    <SelectTrigger className="bg-transparent border-white/20 text-white h-12">
                      <SelectValue placeholder={t('tshirt.selectGender')} />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/20">
                      <SelectItem value="Male" className="text-white focus:bg-burns-red">{t('tshirt.male')}</SelectItem>
                      <SelectItem value="Female" className="text-white focus:bg-burns-red">{t('tshirt.female')}</SelectItem>
                      <SelectItem value="Other" className="text-white focus:bg-burns-red">{t('tshirt.other')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="age" className="text-zinc-400 mb-2 block">{t('registration.age')} *</Label>
                  <Input
                    id="age"
                    type="number"
                    min="8"
                    max="18"
                    value={formData.age}
                    onChange={(e) => handleChange("age", e.target.value)}
                    className="form-input bg-transparent border-b border-white/20 rounded-none focus:border-burns-red"
                    required
                    data-testid="age-input"
                  />
                </div>
                <div>
                  <Label htmlFor="skill_level" className="text-zinc-400 mb-2 block">{t('registration.skillLevel')} *</Label>
                  <Select value={formData.skill_level} onValueChange={(v) => handleChange("skill_level", v)} data-testid="skill-level-select">
                    <SelectTrigger className="bg-transparent border-white/20 text-white h-12">
                      <SelectValue placeholder={t('registration.selectSkillLevel')} />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/20">
                      <SelectItem value="Beginner" className="text-white focus:bg-burns-red">{t('registration.beginner')}</SelectItem>
                      <SelectItem value="Intermediate" className="text-white focus:bg-burns-red">{t('registration.intermediate')}</SelectItem>
                      <SelectItem value="Advanced" className="text-white focus:bg-burns-red">{t('registration.advanced')}</SelectItem>
                      <SelectItem value="Elite" className="text-white focus:bg-burns-red">Elite</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Parent/Guardian Information */}
            <div className="bg-zinc-950 border border-white/10 rounded-sm p-6">
              <h2 className="font-heading text-xl font-bold text-burns-gold mb-4">{t('registration.parentInfo')}</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="parent_name" className="text-zinc-400 mb-2 block">{t('registration.parentName')} *</Label>
                  <Input
                    id="parent_name"
                    type="text"
                    value={formData.parent_name}
                    onChange={(e) => handleChange("parent_name", e.target.value)}
                    className="form-input bg-transparent border-b border-white/20 rounded-none focus:border-burns-red"
                    required
                    data-testid="parent-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="parent_email" className="text-zinc-400 mb-2 block">{t('registration.email')} *</Label>
                  <Input
                    id="parent_email"
                    type="email"
                    value={formData.parent_email}
                    onChange={(e) => handleChange("parent_email", e.target.value)}
                    className="form-input bg-transparent border-b border-white/20 rounded-none focus:border-burns-red"
                    required
                    data-testid="parent-email-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="parent_phone" className="text-zinc-400 mb-2 block">{t('registration.phone')} *</Label>
                  <Input
                    id="parent_phone"
                    type="tel"
                    value={formData.parent_phone}
                    onChange={(e) => handleChange("parent_phone", e.target.value)}
                    className="form-input bg-transparent border-b border-white/20 rounded-none focus:border-burns-red"
                    required
                    data-testid="parent-phone-input"
                  />
                </div>
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="bg-zinc-950 border border-white/10 rounded-sm p-6">
              <h2 className="font-heading text-xl font-bold text-burns-gold mb-4">{t('registration.emergencyInfo')}</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="emergency_contact_name" className="text-zinc-400 mb-2 block">{t('registration.emergencyName')} *</Label>
                  <Input
                    id="emergency_contact_name"
                    type="text"
                    value={formData.emergency_contact_name}
                    onChange={(e) => handleChange("emergency_contact_name", e.target.value)}
                    className="form-input bg-transparent border-b border-white/20 rounded-none focus:border-burns-red"
                    required
                    data-testid="emergency-contact-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="emergency_contact_phone" className="text-zinc-400 mb-2 block">{t('registration.emergencyPhone')} *</Label>
                  <Input
                    id="emergency_contact_phone"
                    type="tel"
                    value={formData.emergency_contact_phone}
                    onChange={(e) => handleChange("emergency_contact_phone", e.target.value)}
                    className="form-input bg-transparent border-b border-white/20 rounded-none focus:border-burns-red"
                    required
                    data-testid="emergency-contact-phone-input"
                  />
                </div>
              </div>
            </div>

            {/* Medical Information */}
            <div className="bg-zinc-950 border border-white/10 rounded-sm p-6">
              <h2 className="font-heading text-xl font-bold text-burns-gold mb-4">{t('registration.medicalInfo')}</h2>
              <div>
                <Label htmlFor="medical_info" className="text-zinc-400 mb-2 block">
                  {t('registration.medicalPlaceholder')}
                </Label>
                <Textarea
                  id="medical_info"
                  value={formData.medical_info}
                  onChange={(e) => handleChange("medical_info", e.target.value)}
                  className="bg-transparent border border-white/20 rounded-sm min-h-[100px] text-white focus:border-burns-red"
                  placeholder={t('registration.medicalPlaceholder')}
                  data-testid="medical-info-input"
                />
              </div>
            </div>

            {/* Digital Waiver */}
            <div className="bg-zinc-950 border border-burns-red/30 rounded-sm p-6" data-testid="waiver-section">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-6 h-6 text-burns-red" />
                <h2 className="font-heading text-xl font-bold text-burns-gold">{t('registration.waiver')} *</h2>
              </div>
              
              <button
                type="button"
                onClick={() => setShowWaiver(!showWaiver)}
                className="text-burns-gold underline text-sm mb-4 hover:text-white transition-colors"
              >
                {showWaiver ? t('registration.hideWaiver') : t('registration.readWaiver')}
              </button>
              
              {showWaiver && (
                <div className="bg-burns-dark p-4 rounded-sm mb-4 max-h-60 overflow-y-auto">
                  <p className="text-zinc-400 text-sm whitespace-pre-line">{t('registration.waiverText')}</p>
                </div>
              )}
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="waiver_signed"
                    checked={formData.waiver_signed}
                    onCheckedChange={(checked) => handleChange("waiver_signed", checked)}
                    className="mt-1 border-burns-red data-[state=checked]:bg-burns-red"
                    data-testid="waiver-checkbox"
                  />
                  <Label htmlFor="waiver_signed" className="text-zinc-300 text-sm cursor-pointer">
                    {t('registration.agreeWaiver')}
                  </Label>
                </div>
                
                <div>
                  <Label htmlFor="waiver_signer_name" className="text-zinc-400 mb-2 block">
                    {t('registration.signerName')} *
                  </Label>
                  <Input
                    id="waiver_signer_name"
                    type="text"
                    value={formData.waiver_signer_name}
                    onChange={(e) => handleChange("waiver_signer_name", e.target.value)}
                    className="form-input bg-transparent border-b border-white/20 rounded-none focus:border-burns-red"
                    placeholder={t('registration.signerName')}
                    data-testid="waiver-signature-input"
                  />
                </div>
              </div>
            </div>

            {/* Photo consent — opt-in. Stored on the registration record. */}
            <div className="bg-zinc-950 border border-white/10 rounded-sm p-6">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="photo_consent"
                  checked={formData.photo_consent}
                  onCheckedChange={(v) => handleChange("photo_consent", !!v)}
                  className="mt-1 border-white/40 data-[state=checked]:bg-burns-red"
                  data-testid="photo-consent-checkbox"
                />
                <Label htmlFor="photo_consent" className="text-zinc-300 leading-relaxed cursor-pointer">
                  <span className="text-white font-medium">{t('registration.photoConsentTitle')}</span>
                  <span className="block text-zinc-400 text-sm mt-1">
                    {t('registration.photoConsentBody')}
                  </span>
                </Label>
              </div>
            </div>

            {/* Submit Button */}
            <div className="text-center">
              <Button
                type="submit"
                className="btn-primary text-lg px-12 py-6 rounded-sm"
                disabled={isSubmitting || camps.length === 0 || !formData.waiver_signed || !formData.waiver_signer_name}
                data-testid="submit-registration-btn"
              >
                {isSubmitting ? t('registration.submitting') : t('registration.submitRegistration')}
              </Button>
            </div>
          </form>
        </motion.div>
      </main>
    </div>
  );
}
